import React, { useState } from "react";
import { Check, CalendarClock, User, Mail, Phone, ArrowRight, Star } from "lucide-react";
import { IMAGES, REVIEWS } from "../mock";import { useToast } from "../hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const DEFAULT_BULLETS = [
  "TMS for depression, anxiety & OCD",
  "Esketamine for chronic and resistant depression",
  "Bulk-billed TMS for treatment-resistant depression",
  "Individualised treatment planning",
  "Psychiatry, psychology & allied health",
];

const DEFAULT_BADGES = ["NDIS", "Medicare", "DVA", "Work Cover (iCare)", "Work Cover QLD"];

const HELP_OPTIONS = [
  "TMS treatment",
  "Accelerated TMS",
  "Esketamine",
  "Depression",
  "Anxiety",
  "OCD",
];

// Short review shown in full on the cover snippet is chosen via the reviewName prop

export const LOGOS = [
  { src: "/site-images/logo-ndis.png", alt: "NDIS registered provider", bg: "#6C2B77", img: "h-8 sm:h-9" },
  { src: "/site-images/logo-medicare.png", alt: "Medicare", bg: "#00956A", img: "h-11 sm:h-[3.25rem]" },
  { src: "/site-images/logo-dva.png", alt: "Department of Veterans' Affairs", bg: "#1E2B5F", img: "h-[2.9rem] sm:h-[3.3rem]", pad: "px-1.5 py-1" },
  { src: "/site-images/logo-workcover-qld.jpg", alt: "WorkCover Queensland", bg: "#EAE9EE", img: "h-7 sm:h-[2.1rem]" },
  { src: "/site-images/logo-icare.jpg", alt: "icare Insurance and Care NSW", bg: "#FFC72C", img: "h-9 sm:h-11" },
];

export default function HeroCover({
  title = "TMS & Esketamine Clinic in Sydney & Bundaberg",
  subtitle = "A professional team providing advanced evidence-based treatments within a caring, supportive environment.",
  bullets = DEFAULT_BULLETS,
  badges = DEFAULT_BADGES,
  image = IMAGES.reception,
  defaultHelp = "",
  showRibbon = true,
  reviewName = "Carla",
  showReview = true,
  inlineLogos = [],
}) {
  const { toast } = useToast();
  const coverReview =
    REVIEWS.find((r) => r.name === reviewName) ||
    REVIEWS.find((r) => r.name === "Carla") ||
    REVIEWS[0];
  const [form, setForm] = useState({
    location: "Sydney",
    name: "",
    email: "",
    phone: "",
    help: defaultHelp,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onChange = (e) => set(e.target.name, e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.help) {
      toast({
        title: "Please complete all required fields",
        description: "Name, email, phone and how we can help are required.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "hero" }),
      });
      if (!res.ok) throw new Error("Request failed");
      setForm({ location: "Sydney", name: "", email: "", phone: "", help: defaultHelp, message: "" });
      toast({
        title: "Enquiry sent",
        description: "Thank you. The AwareHub team will get back to you shortly.",
      });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Your enquiry could not be sent. Please try again or call 1300 029 273.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-white/15 border border-white/30 px-4 py-3 text-white placeholder-white/70 outline-none focus:border-white/70 focus:ring-2 focus:ring-white/30 transition";

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden">
      {/* Background cover image */}
      <img
        src={image}
        alt="AwareHub mental health clinic"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* Base darkening tint — lighter overall */}
      <div className="absolute inset-0 bg-brand-950/32" />
      {/* Frame: darker top & bottom, lighter through the central band (title + form) */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-950/60 via-transparent to-brand-950/60" />
      {/* Corner vignette — keeps the corners dark */}
      <div className="absolute inset-0 [background:radial-gradient(ellipse_96%_86%_at_50%_50%,transparent_52%,rgba(8,39,39,0.78)_100%)]" />
      {/* Soft left scrim keeps the title crisp */}
      <div className="absolute inset-0 bg-gradient-to-r from-brand-950/55 via-transparent to-transparent" />
      {/* Vibrant teal spotlight draws attention to the form */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_82%_45%,rgba(15,184,166,0.20),transparent_46%)]" />
      {/* Subtle dot pattern for texture */}
      <div className="absolute inset-0 opacity-[0.09] [background-image:radial-gradient(rgba(255,255,255,0.7)_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="relative w-full max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-36 lg:pt-28 lg:pb-32 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
        {/* Left content */}
        <div className="text-white animate-fade-up">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.08] text-balance">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-5 text-lg text-white/85 max-w-xl leading-relaxed">
              {subtitle}
            </p>
          )}
          <ul className="mt-8 space-y-3.5">
            {bullets.map((b) => (
              <li key={b} className="flex items-center gap-3 text-white/95">
                <span className="h-6 w-6 rounded-full bg-brand-400 flex items-center justify-center shrink-0">
                  <Check className="h-4 w-4 text-white" strokeWidth={3} />
                </span>
                <span className="text-[1.02rem]">{b}</span>
              </li>
            ))}
          </ul>
          {badges.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-3">
              {badges.map((b) => (
                <span
                  key={b}
                  className="rounded-full bg-white/90 text-brand-800 text-sm font-semibold px-4 py-2 shadow-sm"
                >
                  {b}
                </span>
              ))}
            </div>
          )}

          {inlineLogos.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-inline-logos">
              {inlineLogos.map((l) => (
                <LogoChip key={l.alt} l={l} />
              ))}
            </div>
          )}

          {/* Review snippet — social proof */}
          {showReview && (
          <div className="mt-8 max-w-md rounded-2xl bg-white/12 backdrop-blur-md border border-white/25 p-4">
            <div className="flex items-center gap-1 text-[#FBBC05]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4" fill="#FBBC05" />
              ))}
              <span className="ml-2 text-white/85 text-xs font-semibold uppercase tracking-wider">5.0 · Google</span>
            </div>
            <p className="mt-2 text-sm text-white/90 leading-relaxed italic">
              &ldquo;{coverReview.text}&rdquo;
            </p>
            <p className="mt-1.5 text-xs text-white/70">— {coverReview.name}</p>
          </div>
          )}
        </div>

        {/* Right enquiry form */}
        <div className="w-full lg:justify-self-end lg:max-w-md animate-fade-in">
          <div className="rounded-3xl bg-brand-950/45 backdrop-blur-lg border border-white/25 p-6 sm:p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]">
            <div className="flex items-center gap-3">
              <span className="h-12 w-12 rounded-xl bg-brand-400 flex items-center justify-center shrink-0">
                <CalendarClock className="h-6 w-6 text-white" />
              </span>
              <h2 className="font-display text-2xl sm:text-[1.7rem] text-white tracking-wide">
                Enquire Now
              </h2>
            </div>
            <p className="mt-2 text-white/75 text-sm">
              Fill in your details and we'll get back to you
            </p>

            <form onSubmit={handleSubmit} className="mt-5 space-y-3.5">
              {/* Location radios */}
              <div className="flex items-center gap-6 text-white/90 text-sm">
                {["Sydney", "Bundaberg"].map((loc) => (
                  <label key={loc} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="location"
                      value={loc}
                      checked={form.location === loc}
                      onChange={onChange}
                      className="accent-brand-300 h-4 w-4"
                    />
                    {loc}
                  </label>
                ))}
              </div>

              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-white/70" />
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Your Name*"
                  className={`${inputCls} pl-11`}
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-white/70" />
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="Your Email*"
                  className={`${inputCls} pl-11`}
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-white/70" />
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="Your Phone*"
                  className={`${inputCls} pl-11`}
                />
              </div>

              <Select value={form.help} onValueChange={(v) => set("help", v)}>
                <SelectTrigger className="w-full rounded-xl bg-white/15 border border-white/30 px-4 py-3 h-auto text-base text-white data-[placeholder]:text-white/70 focus:ring-2 focus:ring-white/30">
                  <SelectValue placeholder="How can we help you? *" />
                </SelectTrigger>
                <SelectContent>
                  {HELP_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={3}
                placeholder="Any additional information to assist you best?"
                className={`${inputCls} resize-none`}
              />

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-cta-500 hover:bg-cta-600 disabled:opacity-60 text-white px-6 py-3.5 font-semibold tracking-wide uppercase transition-colors"
              >
                {submitting ? "Sending..." : "Submit"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Accreditation ribbon at the bottom of the cover */}
      {showRibbon && (
        <div className="absolute inset-x-0 bottom-0 z-10 border-t border-white/15 bg-brand-950/40 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex flex-wrap items-center justify-center gap-x-4 sm:gap-x-6 gap-y-2.5">
            {LOGOS.map((l) => (
              <LogoChip key={l.alt} l={l} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function LogoChip({ l }) {
  return (
    <span
      style={{ backgroundColor: l.bg }}
      className={`inline-flex items-center justify-center rounded-lg ${l.pad || "px-3.5 py-2"} h-14 sm:h-16 shadow-[0_10px_28px_-10px_rgba(0,0,0,0.55)] ring-1 ring-white/10`}
    >
      <img src={l.src} alt={l.alt} className={`${l.img} w-auto object-contain`} />
    </span>
  );
}
